// ─────────────────────────────────────────────
// Staff Induction Checklist — single source of truth
// Mirrors "Car Wash Staff Induction Checklist" document exactly.
// Used by StaffInductionForm (fill) and StaffInductionDetail (view).
// ─────────────────────────────────────────────

export const INDUCTEE_FIELDS = [
  { key: 'workerName',       label: 'Worker name',                              required: true },
  { key: 'position',          label: 'Position / role',                          required: true },
  { key: 'startDate',         label: 'Start date',                               required: true, type: 'date' },
  { key: 'site',              label: 'Site',                                     required: true, type: 'site' },
  { key: 'inductedBy',        label: 'Inducted by (supervisor)',                 required: true },
  { key: 'emergencyContact',  label: 'Emergency contact (name + phone)',         required: true },
  { key: 'medicalNotes',      label: 'Known allergies or medical needs to share', required: false, type: 'textarea' },
];

export const PPE_TABLE = [
  ['General washing and drying', 'Non-slip enclosed footwear, waterproof apron or jacket'],
  ['Pressure washing', 'Safety glasses or face shield, non-slip boots, waterproof clothing, hearing protection if loud'],
  ['Mixing, decanting or diluting chemicals', 'Chemical splash goggles or face shield, nitrile or chemical-resistant gloves, apron'],
  ['Wheel acid, degreaser or strong alkaline products', 'Splash goggles, chemical-resistant gauntlet gloves, apron, boots'],
  ['Ceramic coating and solvent products', 'Nitrile gloves, safety glasses, respirator if the SDS requires it, good ventilation'],
  ['Machine polishing', 'Safety glasses, hearing protection, dust mask if dusty'],
  ['Interior vacuuming and steam cleaning', 'Gloves, safety glasses, dust mask for heavy dust'],
  ['Outdoor work', 'Hat, sunscreen, long sleeves'],
];

export const CHECKLIST_SECTIONS = [
  {
    key: 'siteOrientation', parent: null,
    title: 'Site orientation and emergencies',
    subtitle: 'The worker must know how to get out, who to call and where the emergency equipment is before touching any equipment.',
    items: [
      'Toured the full site: wash bays, detailing area, chemical store, plant room, office, toilets and break area',
      'Shown all emergency exits and the evacuation assembly point',
      'Knows the emergency number (000) and the site address to give the operator',
      'Shown the location of fire extinguishers and fire blanket, and knows which types suit electrical fires',
      'Shown the main electrical switchboard and emergency stop (E-stop) buttons for wash equipment',
      'Shown the main water shut-off valve',
      'Knows who the first aid officer and supervisor on shift are',
      'Knows how to report a hazard, near miss or injury, and where the incident report form is kept',
      'Understands that unsafe work can be stopped at any time without penalty',
      'Read and understood the site WHS policy (WA Work Health and Safety Act 2020)',
    ],
  },
  {
    key: 'slipTripFall', parent: null,
    title: 'Slip, trip and fall hazards',
    subtitle: "Wet, soapy floors are the most common cause of injury in a car wash, so housekeeping is everyone's job, every shift.",
    items: [
      'Wears enclosed, non-slip, water-resistant footwear at all times on the wash floor',
      'Knows that soap, wax, tyre shine and degreaser make floors far more slippery than water alone',
      'Squeegees standing water and pooled chemicals towards drains after each vehicle',
      'Places wet floor signs when floors are wet near walkways, the office or customer areas',
      'Checks drains and grates are in place, unblocked and level, and reports any lifted or missing grates',
      'Coils pressure hoses, vacuum hoses and extension leads after use, never leaving them across walkways',
      'Uses hose reels and hooks where provided',
      'Keeps buckets, trolleys, mitts and towels out of walkways and vehicle paths',
      'Walks, never runs, on wet surfaces and takes care stepping over kerbs, ramps and floor tracks',
      'Knows not to climb on vehicle bumpers, tyres or bonnets; uses a stable platform or step for roofs and large vehicles',
      'Cleans up oil and fluid leaks from vehicles using the spill kit',
      'Reports damaged flooring, poor lighting or broken anti-slip strips to the supervisor',
    ],
  },
  {
    key: 'electrical', parent: null,
    title: 'Electrical hazards',
    subtitle: 'Water and electricity are used side by side all day, so every lead, plug and machine is checked before use and kept away from water.',
    items: [
      'Knows that all power outlets used in wash areas must be protected by an RCD (safety switch), and how to test it with the test button',
      'Inspects leads, plugs and equipment before each use for cuts, exposed wires, cracked casings, burn marks or missing pins',
      'Checks equipment has a current test and tag, and does not use anything out of date or tagged "Out of Service"',
      'Never uses electrical equipment with wet hands or while standing in water',
      'Keeps plugs, power boards and lead connections off the ground and away from spray and puddles',
      'Uses only outdoor-rated, heavy-duty extension leads, fully unrolled, and never daisy-chains power boards',
      'Never sprays water or chemicals at power points, switchboards, lights, vacuums or machine control panels',
      'Switches off and unplugs equipment before cleaning, clearing a blockage or changing attachments',
      'Knows where the switchboard is and never opens it or attempts electrical repairs (licensed electrician only)',
      'Knows to isolate power before helping anyone who has had an electric shock, and never touches them while they are in contact with the source',
      'Stops work and reports any tingle, shock, sparking, burning smell or tripping safety switch immediately',
      'Knows the lockout/tag-out procedure for faulty machines',
    ],
  },
  {
    key: 'chemLabelling', parent: 'Safe chemical handling',
    title: 'Information and labelling',
    subtitle: 'Car wash chemicals (degreasers, wheel acids, alkaline pre-wash, ceramic coatings, solvents) can burn skin and eyes or harm lungs, so every product is used only as its Safety Data Sheet (SDS) directs.',
    items: [
      'Shown the SDS register and knows how to find the SDS for every product on site',
      'Can read a product label: product name, hazard pictograms, signal word (Danger/Warning), PPE and first aid',
      'Knows never to use a product with no label, and to label every decanted or diluted container with the product name and hazards',
      'Knows never to put chemicals in drink bottles or food containers',
    ],
  },
  {
    key: 'chemMixing', parent: 'Safe chemical handling',
    title: 'Mixing and dilution',
    items: [
      'Uses dilution stations or measuring jugs exactly as the label or SDS states, never "by eye"',
      'Always adds chemical to water, never water to chemical',
      'Never mixes products together, especially acids with alkalis or anything with bleach',
      'Mixes and decants only in the designated, ventilated area while wearing the required PPE',
    ],
  },
  {
    key: 'chemUse', parent: 'Safe chemical handling',
    title: 'Use',
    items: [
      'Applies chemicals to cool surfaces, out of direct sun, and avoids overspray towards people',
      'Keeps customers and other staff clear of the area when spraying',
      'Uses ventilation or works outdoors when using solvent-based products or coatings',
    ],
  },
  {
    key: 'chemStorage', parent: 'Safe chemical handling',
    title: 'Storage',
    items: [
      'Returns chemicals to the chemical store after use with lids tightly closed',
      'Stores acids and alkalis separately, on bunded shelving or spill trays, below eye level',
      'Keeps flammable products (solvents, some coatings) away from heat, sparks and ignition sources',
    ],
  },
  {
    key: 'chemSpills', parent: 'Safe chemical handling',
    title: 'Spills and disposal',
    items: [
      'Shown the spill kit location and how to use it (contain, absorb, bag, report)',
      'Knows to report any large spill to the supervisor straight away',
      "Knows that wash water and chemicals must go to the site's approved drain or trap, never to stormwater",
      'Disposes of empty containers and used absorbent as the SDS and site procedure direct',
    ],
  },
  {
    key: 'ppe', parent: null,
    title: 'Personal protective equipment (PPE)',
    subtitle: 'PPE is the last line of defence and is worn every time the task calls for it; the SDS overrides this table if it asks for more.',
    table: PPE_TABLE,
    items: [
      'Issued personal PPE and shown where spare PPE is kept',
      'Shown how to fit, wear and remove gloves, goggles and respirators correctly',
      'Checks PPE before each use and replaces anything torn, cracked or contaminated',
      'Cleans and stores reusable PPE after use, away from chemicals',
      'Understands that normal sunglasses and prescription glasses are not safety eyewear',
      'Knows how to request replacement PPE at no cost',
    ],
  },
  {
    key: 'eyeWash', parent: 'Eye wash station and first aid',
    title: 'Eye wash station',
    subtitle: 'A chemical splash to the eye needs flushing within seconds, so every worker must be able to reach and use the eye wash without help.',
    items: [
      'Shown every eye wash station and emergency shower on site, and walked the route from the wash bays and chemical store',
      'Knows the route must be kept clear at all times',
      'Has activated the eye wash and seen the flow',
      'Knows the procedure: get to the eye wash immediately, hold eyelids open, roll the eyes, and flush for at least 15 minutes (or as long as the SDS directs)',
      'Knows to remove contact lenses while flushing if they come out easily',
      "Knows to call for help, then 000 or medical attention, taking the product's SDS or label",
      'Knows to use the emergency shower or hose for large skin splashes and to remove contaminated clothing',
      'Knows eye wash stations are flushed and checked weekly, and reports any that are empty, dirty or out of date',
    ],
  },
  {
    key: 'firstAid', parent: 'Eye wash station and first aid',
    title: 'First aid',
    items: [
      'Shown the first aid kit location and contents',
      'Knows who the trained first aid officer is on each shift',
      'Knows to report all injuries, however minor, and record them in the incident register',
    ],
  },
  {
    key: 'pressureEquip', parent: 'Other site hazards',
    title: 'Pressure washers and equipment',
    items: [
      'Trained on each pressure washer, foam cannon, vacuum and polisher before using it',
      'Never points a pressure lance at people, animals or own body, and keeps the trigger lock on when not spraying',
      'Checks high-pressure hoses and fittings for damage before use',
      'Releases pressure before disconnecting hoses or changing nozzles',
    ],
  },
  {
    key: 'vehicleMovement', parent: 'Other site hazards',
    title: 'Vehicle movement',
    items: [
      'Stays clear of vehicles entering, leaving or moving through the bay, and never stands behind a reversing vehicle',
      'Uses hand signals or a spotter to guide customer vehicles',
      'Only drives customer vehicles if authorised and licensed, at walking pace, with the keys secured afterwards',
    ],
  },
  {
    key: 'manualHandling', parent: 'Other site hazards',
    title: 'Manual handling',
    items: [
      'Lifts chemical drums and heavy items with a trolley or a second person',
      'Uses correct posture: bend knees, keep load close, avoid twisting',
      'Rotates tasks and takes breaks to avoid repetitive strain from polishing and scrubbing',
    ],
  },
  {
    key: 'heatSun', parent: 'Other site hazards',
    title: 'Heat, sun and fatigue',
    items: [
      'Drinks water regularly and takes shade breaks on hot days',
      'Knows the signs of heat stress and reports feeling unwell',
    ],
  },
  {
    key: 'customersPublic', parent: 'Other site hazards',
    title: 'Customers and public',
    items: [
      'Keeps customers out of wash bays and chemical areas',
    ],
  },
];

export const DECLARATION_TEXT = 'I have completed this induction, had each item explained and demonstrated, and had the chance to ask questions. I understand the hazards on this site and agree to follow the safe work procedures and wear the required PPE.';

export const RETENTION_NOTE = "Keep the signed copy on the worker's file. Repeat the induction if the worker is away for more than 6 months, or when new chemicals, equipment or procedures are introduced.";

export const TOTAL_CHECKLIST_ITEMS = CHECKLIST_SECTIONS.reduce((sum, s) => sum + s.items.length, 0);

export const buildEmptyChecklist = () =>
  Object.fromEntries(CHECKLIST_SECTIONS.map(s => [s.key, s.items.map(() => false)]));

export const countChecked = (checklist) =>
  CHECKLIST_SECTIONS.reduce((sum, s) => sum + (checklist?.[s.key] || []).filter(Boolean).length, 0);

// Groups CHECKLIST_SECTIONS into wizard steps — sections sharing a `parent`
// become one step (e.g. all 5 "Safe chemical handling" sub-sections), and
// standalone sections (parent: null) each become their own step.
export const CHECKLIST_STEPS = (() => {
  const steps = [];
  const parentIndex = {};
  CHECKLIST_SECTIONS.forEach(section => {
    if (section.parent) {
      if (!(section.parent in parentIndex)) {
        parentIndex[section.parent] = steps.length;
        steps.push({ title: section.parent, sectionKeys: [section.key] });
      } else {
        steps[parentIndex[section.parent]].sectionKeys.push(section.key);
      }
    } else {
      steps.push({ title: section.title, sectionKeys: [section.key] });
    }
  });
  return steps;
})();
